import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useHighscores } from '@/hooks/useHighscores';
import { formatNumber } from '@/lib/utils';
import { useTranslation } from '@/i18n';
import type { GameState } from './types';

interface EndModalProps {
  gameState: GameState;
  onSubmit: (name: string, email?: string) => Promise<void>;
  onPlayAgain: () => void;
  isSubmitting: boolean;
}

export function EndModal({ gameState, onSubmit, onPlayAgain, isSubmitting }: EndModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation();
  
  const { data: highscores } = useHighscores('cabinet-transformer', 10);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    await onSubmit(name.trim(), email.trim() || undefined);
    setSubmitted(true);
  };

  // Calculate rank
  const rank = highscores 
    ? highscores.filter(h => h.score > gameState.score).length + 1
    : '?';

  return (
    <div className="modal-backdrop animate-fade-in p-2 sm:p-4">
      <div className="card max-w-2xl w-full animate-scale-in overflow-hidden max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)]">
        <div className="flex flex-col md:flex-row max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)] overflow-y-auto">
          {/* Left: Score Summary */}
          <div className="p-4 sm:p-8 flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-2">
              {t.cabinetTransformer.gameFinished}
            </h2>
            <p className="text-text-secondary text-sm sm:text-base mb-4 sm:mb-6">{t.cabinetTransformer.wellDone}</p>

            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              <div>
                <span className="text-text-muted text-xs sm:text-sm uppercase">{t.cabinetTransformer.yourScore}</span>
                <div className="text-3xl sm:text-4xl font-bold text-text-primary">
                  {formatNumber(gameState.score)}
                </div>
              </div>

              <div>
                <span className="text-text-muted text-xs sm:text-sm uppercase">{t.cabinetTransformer.transformedCabinets}</span>
                <div className="text-xl sm:text-2xl font-bold text-text-primary">
                  {gameState.hitCabinets}
                </div>
              </div>

              <div>
                <span className="text-text-muted text-xs sm:text-sm uppercase">{t.cabinetTransformer.yourRank}</span>
                <div className="text-xl sm:text-2xl font-bold text-text-primary">
                  {t.common.rank} {rank} {t.common.of} {(highscores?.length || 0) + 1}
                </div>
              </div>
            </div>

            <button onClick={onPlayAgain} className="btn-primary w-full text-sm sm:text-base py-2.5 sm:py-3">
              {t.cabinetTransformer.keepHunting}
            </button>
            <Link to="/" className="btn-secondary w-full mt-2 sm:mt-3 text-center text-sm sm:text-base py-2.5 sm:py-3">
              {t.common.backToHome}
            </Link>
          </div>

          {/* Right: Highscore Form */}
          <div className="p-4 sm:p-8 flex-1 bg-bg-deep border-t md:border-t-0 md:border-l border-border-dark">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4 flex items-center gap-2">
              🏆 {t.common.highscores}
            </h3>

            {/* Highscore List */}
            {highscores && highscores.length > 0 && (
              <ol className="space-y-2 mb-4 sm:mb-6 max-h-24 sm:max-h-32 overflow-y-auto">
                {highscores.slice(0, 5).map((entry, index) => (
                  <li key={entry.id} className="flex justify-between text-xs sm:text-sm">
                    <span className="text-text-secondary">
                      {index + 1}. {entry.player_name}
                    </span>
                    <span className="font-semibold text-text-primary">
                      {formatNumber(entry.score)}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="bg-accent-gold/10 border border-accent-gold/30 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
                  <p className="text-xs sm:text-sm">
                    <strong className="text-accent-gold">{t.cabinetTransformer.howToWin}</strong>
                    <span className="text-text-secondary ml-1">
                      {t.cabinetTransformer.winText}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="text-text-muted text-xs sm:text-sm block mb-1">{t.common.name}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.cabinetTransformer.enterName}
                    className="input text-sm sm:text-base"
                    maxLength={40}
                    required
                  />
                </div>

                <div>
                  <label className="text-text-muted text-xs sm:text-sm block mb-1">{t.common.email}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.cabinetTransformer.enterEmail}
                    className="input text-sm sm:text-base"
                    maxLength={80}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full text-sm sm:text-base py-2.5 sm:py-3"
                  disabled={isSubmitting || !name.trim()}
                >
                  {isSubmitting ? t.common.saving : t.cabinetTransformer.saveScore}
                </button>
              </form>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <span className="text-3xl sm:text-4xl mb-3 sm:mb-4 block">🎉</span>
                <p className="text-ukonnx-green font-semibold text-sm sm:text-base">{t.common.scoreSaved}</p>
                <p className="text-text-secondary text-xs sm:text-sm">{t.common.goodLuck}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
